import React, { useEffect, useRef, useState } from 'react';
import { InstituteAutomationService, InstituteRow, MergedOverrideRow, SectionOverrideRow } from '../instituteAutomationService';

const MONTHS_TO_LABEL: Record<number, string> = { 1: 'Monthly', 3: 'Quarterly', 6: 'Semi-Annual', 12: 'Annual' };
const LABEL_TO_MONTHS: Record<string, number> = { Monthly: 1, Quarterly: 3, 'Semi-Annual': 6, Annual: 12 };
const FREQ_LABELS = ['Monthly', 'Quarterly', 'Semi-Annual', 'Annual'];
const freqLabel = (m: number) => MONTHS_TO_LABEL[m] ?? `${m}m`;
// Frequency label including the Skip state (0 = Skip).
const freqLbl = (m: number): string => (m === 0 ? 'Skip' : (MONTHS_TO_LABEL[m] ?? `${m}m`));
// Trim trailing decimal zeros: "3.0000" → "3".
const trimNum = (v: string | number): string => {
    const s = String(v);
    return /^-?\d+\.\d+$/.test(s) ? String(Number(s)) : s;
};

// Per-row editable override state.
interface RowEdit { on: boolean; freqMonths: number; include: 'yes' | 'no'; minValue: string; notes: string }
const seedEdit = (r: MergedOverrideRow): RowEdit => ({
    on: r.isClientOverride,
    freqMonths: r.override?.customFrequencyMonths ?? r.global.frequencyMonths,
    include: r.override?.includeInReport === 'no' ? 'no' : 'yes',
    minValue: r.override?.customMinValue != null ? trimNum(r.override.customMinValue) : (r.global.minValue != null ? trimNum(r.global.minValue) : ''),
    notes: r.override?.smNotes ?? '',
});

/** Live searchable institute combobox (reuses institute search). */
const ClientSearch: React.FC<{ selected: InstituteRow | null; onSelect: (c: InstituteRow) => void }> = ({ selected, onSelect }) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<InstituteRow[]>([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const onDocClick = (e: MouseEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false); };
        document.addEventListener('click', onDocClick);
        return () => document.removeEventListener('click', onDocClick);
    }, []);

    // Debounced live search.
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        const t = setTimeout(async () => {
            try {
                const rows = await InstituteAutomationService.searchInstitutes(query.trim(), 20);
                if (!cancelled) setResults(rows);
            } catch (_) {
                if (!cancelled) setResults([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, query ? 350 : 0);
        return () => { cancelled = true; clearTimeout(t); };
    }, [query]);

    return (
        <div className="d-flex flex-column gap-1 position-relative" ref={wrapRef}>
            <label className="text-uppercase text-secondary font-10 font-600 tracking-wide">Select Client</label>
            <div className="input-group input-group-sm">
                <span className="input-group-text bg-white border-end-0 text-secondary"><i className="bi bi-search" /></span>
                <input
                    type="text" className="form-control form-control-sm font-13 py-2 ps-0 border-start-0"
                    placeholder={selected ? selected.name : 'Search client by name…'} autoComplete="off"
                    value={query} onFocus={() => setOpen(true)} onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                    style={{ boxShadow: 'none' }}
                />
            </div>
            {open && (
                <ul className="dropdown-menu show w-100 shadow border border-light-subtle py-1 font-13" style={{ position: 'absolute', top: '100%', left: 0, zIndex: 1050, maxHeight: 260, overflowY: 'auto' }}>
                    {loading ? (
                        <li className="dropdown-item py-2 text-muted font-13"><span className="spinner-border spinner-border-sm me-2" />Searching…</li>
                    ) : results.length === 0 ? (
                        <li className="dropdown-item py-2 text-muted font-13">No clients found</li>
                    ) : results.map((c) => (
                        <li key={c.id}>
                            <a className={`dropdown-item py-2 font-13 ${c.id === selected?.id ? 'active' : ''}`} href="#" onClick={(e) => { e.preventDefault(); onSelect(c); setOpen(false); setQuery(''); }}>
                                {c.name} <span className="text-secondary font-10">· {c.id}</span>
                            </a>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

/** One override row — controlled; edits flow up via onChange. */
const OverrideRow: React.FC<{ row: MergedOverrideRow; edit: RowEdit; onChange: (patch: Partial<RowEdit>) => void }> = ({ row, edit, onChange }) => {
    const disabled = !edit.on;
    return (
        <tr className="border-bottom border-light" style={edit.on ? { backgroundColor: '#fffbf2' } : undefined}>
            <td className="ps-4 py-3">
                <strong className="text-dark d-block">{row.name}</strong>
                <span className="text-secondary font-10">{row.section}</span>
            </td>
            <td><span className="badge bg-light text-secondary font-10 font-500 px-2 py-1 rounded-pill">{row.section}</span></td>
            <td><span className="badge bg-light text-secondary font-10 font-500 px-2 py-1 rounded-pill">{freqLabel(row.global.frequencyMonths)}</span></td>
            <td>
                <div className="form-check form-switch mb-0">
                    <input className="form-check-input" type="checkbox" role="switch" checked={edit.on} onChange={(e) => onChange({ on: e.target.checked })} />
                </div>
            </td>
            <td>
                <select className="form-select form-select-sm font-11 py-1 px-2 border" style={{ width: 'auto' }} value={freqLabel(edit.freqMonths)} disabled={disabled} onChange={(e) => onChange({ freqMonths: LABEL_TO_MONTHS[e.target.value] ?? edit.freqMonths })}>
                    {FREQ_LABELS.map((o) => <option key={o}>{o}</option>)}
                </select>
            </td>
            <td>
                <div className="d-flex align-items-center gap-2">
                    <div className="form-check form-switch mb-0">
                        <input className="form-check-input" type="checkbox" role="switch" checked={edit.include === 'yes'} disabled={disabled} onChange={(e) => onChange({ include: e.target.checked ? 'yes' : 'no' })} />
                    </div>
                    <span className={`font-11 font-600 ${edit.include === 'yes' ? 'text-success' : 'text-danger'}`}>{edit.include === 'yes' ? 'YES' : 'NO — Skip'}</span>
                </div>
            </td>
            <td><input className="form-control form-control-sm font-11 py-1 px-2 text-center border" type="text" value={edit.minValue} placeholder="—" style={{ width: 80 }} disabled={disabled} onChange={(e) => onChange({ minValue: e.target.value })} /></td>
            <td className="pe-4"><input className="form-control form-control-sm font-11 py-1 px-2 border" type="text" value={edit.notes} placeholder="—" style={{ width: 160 }} disabled={disabled} onChange={(e) => onChange({ notes: e.target.value })} /></td>
        </tr>
    );
};

const ClientSettings: React.FC<{ toast: (msg: string) => void }> = ({ toast }) => {
    const [institute, setInstitute] = useState<InstituteRow | null>(null);
    const [rows, setRows] = useState<MergedOverrideRow[]>([]);
    const [edit, setEdit] = useState<Record<number, RowEdit>>({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [saving, setSaving] = useState(false);
    // Per-section frequency overrides for this client.
    const [sections, setSections] = useState<SectionOverrideRow[]>([]);

    const load = async (inst: InstituteRow) => {
        setLoading(true); setError(false);
        try {
            const [data, secs] = await Promise.all([
                InstituteAutomationService.getInstituteOverrides(inst.id),
                InstituteAutomationService.getSectionOverrides(inst.id).catch(() => [] as SectionOverrideRow[]),
            ]);
            setRows(data);
            setSections(secs);
            const seed: Record<number, RowEdit> = {};
            data.forEach((r) => { seed[r.componentId] = seedEdit(r); });
            setEdit(seed);
        } catch (_) {
            setError(true); setRows([]); setEdit({}); setSections([]);
        } finally {
            setLoading(false);
        }
    };

    // Section frequency override: null = inherit global, 0 = Skip, else 1/3/6/12.
    const setSectionFreq = async (sectionId: number, value: number | null) => {
        setSections((prev) => prev.map((s) => s.sectionId === sectionId
            ? { ...s, customFrequencyMonths: value, isOverride: value != null, effectiveFrequencyMonths: value != null ? value : s.globalFrequencyMonths }
            : s));
        try {
            if (!institute) return;
            await InstituteAutomationService.upsertSectionOverride(institute.id, sectionId, value);
            toast(value == null ? 'Section reset to global' : value === 0 ? 'Section skipped for this client' : 'Section frequency overridden');
        } catch (_) {
            toast('Could not save section frequency');
        }
    };

    const onSelect = (c: InstituteRow) => { setInstitute(c); load(c); };
    const patchRow = (id: number, patch: Partial<RowEdit>) => setEdit((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

    const activeCount = rows.filter((r) => edit[r.componentId]?.on).length;

    const save = async () => {
        if (!institute || saving) return;
        setSaving(true);
        try {
            await Promise.all(rows.map((row) => {
                const e = edit[row.componentId];
                if (e?.on) {
                    return InstituteAutomationService.upsertOverride(institute.id, row.componentId, {
                        custom_frequency_months: e.freqMonths,
                        include_in_report: e.include,
                        custom_min_value: e.minValue.trim() === '' ? null : Number(e.minValue),
                        sm_notes: e.notes.trim() === '' ? null : e.notes,
                    });
                }
                if (row.isClientOverride) return InstituteAutomationService.removeOverride(institute.id, row.componentId);
                return Promise.resolve();
            }));
            toast('Client overrides saved ✔');
            await load(institute);
        } catch (_) {
            toast('Could not save overrides — please try again');
        } finally {
            setSaving(false);
        }
    };

    const reset = async () => {
        if (!institute || saving) return;
        setSaving(true);
        try {
            await InstituteAutomationService.resetOverrides(institute.id);
            toast('All overrides reset to global defaults');
            await load(institute);
        } catch (_) {
            toast('Could not reset overrides');
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <div className="card border border-light shadow-sm rounded-3 p-4 mb-4 bg-white">
                <div className="row align-items-center g-3">
                    <div className="col-12 col-md-4">
                        <ClientSearch selected={institute} onSelect={onSelect} />
                    </div>
                    <div className="col-12 col-md-5">
                        {institute ? (
                            <>
                                <h5 className="font-16 font-600 text-dark mb-1">{institute.name}</h5>
                                <div className="d-flex flex-wrap gap-3 font-11 text-secondary">
                                    <span>🆔 {institute.id}</span>
                                    {institute.city && <span>📍 {institute.city}</span>}
                                    {institute.type && <span>🏫 {institute.type}</span>}
                                    <span>🔧 {activeCount} active override{activeCount === 1 ? '' : 's'}</span>
                                </div>
                            </>
                        ) : (
                            <div className="text-secondary font-13">Search and select a client to view and edit its component overrides.</div>
                        )}
                    </div>
                    <div className="col-12 col-md-3 text-md-end d-flex flex-column gap-2 align-items-md-end">
                        <button className="btn text-white font-12 font-600 px-3 w-100 py-2 border-0" style={{ backgroundColor: '#f97316', maxWidth: 160, opacity: institute && !saving ? 1 : 0.6 }} disabled={!institute || saving} onClick={save}>{saving ? 'Saving…' : 'Save Overrides'}</button>
                        <button className="btn btn-outline-secondary font-12 font-600 px-3 w-100 py-2" style={{ maxWidth: 160 }} disabled={!institute || saving} onClick={reset}>↺ Reset to Global</button>
                    </div>
                </div>
            </div>

            {/* Section-level send frequency for this client */}
            {institute && sections.length > 0 && (
                <div className="card border border-primary-subtle shadow-sm rounded-3 mb-3 bg-white">
                    <div className="card-header bg-white border-bottom py-3 px-4 font-14 font-600 text-dark d-flex align-items-center gap-2">
                        <span className="badge bg-primary bg-opacity-10 text-primary px-2 py-1 font-12">📅</span>
                        Section Send Frequency — {institute.name}
                    </div>
                    <div className="card-body py-2">
                        <div className="font-11 text-secondary mb-2">Override how often each section is emailed for this client. <strong>Global</strong> inherits the global setting; <strong>Skip</strong> excludes the section for this client only.</div>
                        {sections.map((s) => (
                            <div key={s.sectionId} className="d-flex align-items-center justify-content-between border-bottom border-light py-2">
                                <div className="min-w-0">
                                    <div className="font-13 font-600 text-dark">{s.name}{s.isOverride && <span className="badge bg-warning-subtle text-warning-emphasis font-10 ms-2 px-2 py-0.5">Overridden</span>}</div>
                                    <div className="font-10 text-secondary">Global: {freqLbl(s.globalFrequencyMonths)}</div>
                                </div>
                                <select className="form-select form-select-sm font-12" style={{ width: 'auto' }}
                                    value={s.customFrequencyMonths ?? 'global'}
                                    onChange={(e) => setSectionFreq(s.sectionId, e.target.value === 'global' ? null : Number(e.target.value))}>
                                    <option value="global">Global ({freqLbl(s.globalFrequencyMonths)})</option>
                                    <option value={0}>Skip</option>
                                    <option value={1}>Monthly</option>
                                    <option value={3}>Quarterly</option>
                                    <option value={6}>Semi-Annual</option>
                                    <option value={12}>Annual</option>
                                </select>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="alert alert-info border border-info-subtle rounded-3 p-3 mb-3 d-flex align-items-center gap-2 font-12 text-info-emphasis bg-info-subtle">
                <span>ℹ️</span> Rows with a custom background have client-specific overrides. Toggle <strong>Override ON</strong> to customize a component for this client only.
            </div>

            <div className="card border border-light shadow-sm rounded-3 mb-4 bg-white">
                <div className="card-header bg-white border-bottom py-3 px-4 d-flex justify-content-between align-items-center">
                    <div className="font-14 font-600 text-dark d-flex align-items-center gap-2">
                        <span className="badge bg-warning bg-opacity-10 text-warning px-2 py-1 font-12">🔧</span>
                        Component Override Settings{institute ? <> — <span>{institute.name}</span></> : ''}
                    </div>
                    <span className="text-secondary font-11">Showing overrides vs global defaults</span>
                </div>
                <div className="table-responsive">
                    <table className="table align-middle mb-0 font-12" style={{ minWidth: 900 }}>
                        <thead className="table-light text-secondary text-uppercase font-9 font-600 tracking-wider border-bottom border-light">
                            <tr>
                                <th className="ps-4 py-3" style={{ width: 200 }}>Component</th>
                                <th className="py-3">Section</th>
                                <th className="py-3">Global Default</th>
                                <th className="py-3">Override ON?</th>
                                <th className="py-3">Custom Frequency</th>
                                <th className="py-3">Include in Report?</th>
                                <th className="py-3">Custom Threshold</th>
                                <th className="pe-4 py-3">SM Notes</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!institute ? (
                                <tr><td colSpan={8} className="text-center text-secondary py-5 font-12">Select a client above to load its components.</td></tr>
                            ) : loading ? (
                                <tr><td colSpan={8} className="text-center text-secondary py-5"><span className="spinner-border spinner-border-sm me-2" />Loading components…</td></tr>
                            ) : error ? (
                                <tr><td colSpan={8} className="text-center text-secondary py-5 font-12">⚠️ Couldn't load overrides for this client.</td></tr>
                            ) : rows.length === 0 ? (
                                <tr><td colSpan={8} className="text-center text-secondary py-5 font-12">No components configured yet.</td></tr>
                            ) : rows.map((r) => (
                                <OverrideRow key={r.componentId} row={r} edit={edit[r.componentId]} onChange={(patch) => patchRow(r.componentId, patch)} />
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
};

export default ClientSettings;
